import { detectEntityType, isMongoId } from '../context/EntityResolverContext';
import type {
  TraceEntityNavigationTarget,
  TraceNavigableEntityType,
} from '../context/TraceEntityNavigationContext';
import { filterMcpDisplayArgs } from '../components/tool-args-display.util';
import {
  formatResolvedTraceEntity,
  type ResolveTraceEntity,
} from './inline-tool-summary';

export type InlineToolDetailRow = {
  key: string;
  label: string;
  value: string;
};

type InlineToolDetailsInput = {
  toolName: string;
  args: Record<string, unknown>;
  resolve: ResolveTraceEntity;
};

const DETAIL_VALUE_MAX_LENGTH = 200;

const NAVIGABLE_ARG_KEYS: Record<string, TraceNavigableEntityType> = {
  endpointid: 'endpoint',
  signalid: 'signal',
  findingid: 'finding',
  webpageid: 'webpage',
  executionid: 'execution',
};

const HIDDEN_DETAIL_ARG_KEYS = new Set([
  'query',
  'q',
  'searchquery',
  'limit',
  'topk',
  'k',
  'offset',
  'skip',
  'page',
  'pagesize',
  'perpage',
  'maxresults',
]);

function isIdentifierArg(key: string): boolean {
  return (
    /^ids?$/i.test(key) || /(?:Id|Ids)$/.test(key) || /[_-]ids?$/i.test(key)
  );
}

function normalizeArgKey(key: string): string {
  return key.toLowerCase().replace(/[_-]/g, '');
}

function isHiddenDetailArg(key: string): boolean {
  return (
    isIdentifierArg(key) || HIDDEN_DETAIL_ARG_KEYS.has(normalizeArgKey(key))
  );
}

function formatFieldLabel(name: string): string {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (first) => first.toUpperCase())
    .trim();
}

function truncateDetail(value: string): string {
  return value.length > DETAIL_VALUE_MAX_LENGTH
    ? `${value.slice(0, DETAIL_VALUE_MAX_LENGTH)}…`
    : value;
}

function isEmptyDetailValue(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

function formatDetailValue({
  key,
  value,
  resolve,
}: {
  key: string;
  value: unknown;
  resolve: ResolveTraceEntity;
}): string {
  if (isMongoId(value)) {
    const type = detectEntityType(key);
    const entity = type ? resolve(type, value) : undefined;

    return entity ? formatResolvedTraceEntity(entity) : value;
  }

  if (typeof value === 'string') {
    return truncateDetail(value);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return truncateDetail(JSON.stringify(value));
}

export function getInlineToolDetailRows({
  toolName,
  args,
  resolve,
}: InlineToolDetailsInput): InlineToolDetailRow[] {
  return Object.entries(filterMcpDisplayArgs({ toolName, args }))
    .filter(
      ([key, value]) => !isHiddenDetailArg(key) && !isEmptyDetailValue(value),
    )
    .map(([key, value]) => ({
      key,
      label: formatFieldLabel(key),
      value: formatDetailValue({ key, value, resolve }),
    }));
}

export function getInlineToolNavigationTarget({
  toolName,
  args,
  resolve,
}: InlineToolDetailsInput): TraceEntityNavigationTarget | undefined {
  const match = Object.entries(filterMcpDisplayArgs({ toolName, args })).find(
    ([key, value]) =>
      isMongoId(value) &&
      NAVIGABLE_ARG_KEYS[normalizeArgKey(key)] !== undefined,
  );

  if (!match) {
    return undefined;
  }

  const [key, id] = match;
  const type = NAVIGABLE_ARG_KEYS[normalizeArgKey(key)];

  if (!type || typeof id !== 'string') {
    return undefined;
  }

  const resolvable = type === 'endpoint' || type === 'signal';
  const entity = resolvable ? resolve(type, id) : undefined;

  return {
    type,
    id,
    label: entity ? formatResolvedTraceEntity(entity) : undefined,
  };
}
